import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * RideRequestsCard — admin queue for pending carpool ride requests.
 *
 * Phase A of the rider ↔ admin flow. Polls GET /api/carpool/pending every
 * 3 s and lists requests still in state=searching. Each row shows the
 * pickup label + destination label + wait timer. Accept button POSTs to
 * /accept and auto-picks the nearest driver server-side.
 *
 * Includes a "Simulate rider request" button for demo-day so the
 * presenter can seed a request without the rider app running (Phase B).
 */
import { useEffect, useMemo, useState } from "react";
import { PillButton } from "../common/PillButton";
import { useCarpoolRequests, } from "../../hooks/useCarpoolRequests";
const rootStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 20,
    fontFamily: "var(--font-display)",
};
const summaryStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
};
const cardStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "14px 16px",
    border: "1px solid var(--color-pebble)",
    background: "var(--color-pure-white)",
};
const cardActiveStyle = {
    ...cardStyle,
    background: "var(--color-clay-ember)",
    color: "var(--color-pure-white)",
    border: "1px solid var(--color-clay-ember)",
};
const labelStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    opacity: 0.8,
};
const valueStyle = {
    fontFamily: "var(--font-mono, monospace)",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.35,
    wordBreak: "break-word",
};
function formatWait(iso) {
    const created = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.floor((now - created) / 1000));
    if (s < 60)
        return `${s}s`;
    return `${Math.floor(s / 60)}m ${(s % 60).toString().padStart(2, "0")}s`;
}
function RideRow({ req, isNext, onAccept, accepting }) {
    // Ticking wait timer — re-render every 1 s so the badge stays live.
    const [, tick] = useState(0);
    useEffect(() => {
        const t = window.setInterval(() => tick((v) => v + 1), 1000);
        return () => window.clearInterval(t);
    }, []);
    const pickup = req.pickup.label ?? `${req.pickup.lat.toFixed(3)}, ${req.pickup.lng.toFixed(3)}`;
    const dest = req.destination.label ?? `${req.destination.lat.toFixed(3)}, ${req.destination.lng.toFixed(3)}`;
    const wait = formatWait(req.created_at);
    const isClay = isNext;
    return (_jsxs("div", { style: isClay ? cardActiveStyle : cardStyle, "data-testid": `ride-request-${req.request_id}`, children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", gap: 12 }, children: [_jsx("span", { style: labelStyle, children: "Ride request" }), _jsxs("span", { style: {
                            ...labelStyle,
                            color: isClay ? "var(--color-pure-white)" : "var(--color-cool-ash)",
                        }, children: ["Wait ", wait] })] }), _jsxs("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px" }, children: [_jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "Pickup" }), _jsx("div", { style: valueStyle, children: pickup })] }), _jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "Destination" }), _jsx("div", { style: valueStyle, children: dest })] })] }), _jsx("div", { style: { display: "flex", gap: 8, marginTop: 4 }, children: _jsx(PillButton, { size: "sm", variant: isClay ? "filled" : "outline", onClick: () => onAccept(req.request_id), disabled: accepting, "data-testid": `ride-accept-${req.request_id}`, children: accepting ? "Assigning…" : "Accept & assign" }) })] }));
}
export function RideRequestsCard({ enabled = true, hook }) {
    const local = useCarpoolRequests(enabled && !hook);
    const { pending, loading, lastError, accept, simulate } = hook ?? local;
    const [accepting, setAccepting] = useState(null);
    const [lastAssignment, setLastAssignment] = useState(null);
    const handleAccept = async (id) => {
        setAccepting(id);
        const result = await accept(id);
        setAccepting(null);
        if (result)
            setLastAssignment(result);
    };
    const bodyText = useMemo(() => {
        if (loading && pending.length === 0)
            return "Polling backend…";
        if (lastError)
            return `Error: ${lastError}`;
        if (pending.length === 0)
            return "No pending ride requests. Waiting for a rider.";
        return null;
    }, [loading, lastError, pending.length]);
    return (_jsxs("div", { style: rootStyle, children: [_jsxs("div", { style: summaryStyle, children: [_jsxs("span", { children: [pending.length, " pending"] }), _jsx("span", { children: "Auto-nearest driver" })] }), _jsx(PillButton, { size: "sm", variant: "outline", onClick: () => void simulate(), "data-testid": "ride-simulate", children: "Simulate rider request" }), bodyText ? (_jsx("div", { style: {
                    padding: 16,
                    border: "1px dashed var(--color-pebble)",
                    color: "var(--color-cool-ash)",
                    fontSize: 14,
                    textAlign: "center",
                }, children: bodyText })) : (_jsx("div", { style: { display: "flex", flexDirection: "column", gap: 12 }, children: pending.map((req, i) => (_jsx(RideRow, { req: req, isNext: i === 0, onAccept: handleAccept, accepting: accepting === req.request_id }, req.request_id))) })), lastAssignment?.driver ? (_jsxs("div", { style: {
                    padding: "12px 14px",
                    border: "1px solid var(--color-pebble)",
                    background: "#faf7f0",
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 13,
                }, "data-testid": "last-assignment", children: [_jsx("div", { style: labelStyle, children: "Assigned" }), _jsxs("div", { children: [lastAssignment.driver.name, " \u00B7 ", lastAssignment.driver.vehicle] }), _jsxs("div", { style: { color: "var(--color-cool-ash)" }, children: ["ETA ", lastAssignment.driver.eta_min, " min \u00B7 ", lastAssignment.driver.distance_km, " km"] })] })) : null] }));
}
